import React, { useState, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PageHeader } from "@/components/shared";
import { Disclaimer } from "@/components/Disclaimer";
import { Calculator as CalcIcon } from "lucide-react";

const money = (n) => "$" + Math.round(n).toLocaleString();
const num = (v) => { const n = parseFloat(String(v).replace(/[^0-9.]/g, "")); return isNaN(n) ? 0 : n; };

function Field({ label, value, onChange, prefix, suffix, placeholder, testid }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-sm">{label}</Label>
      <div className="relative">
        {prefix && <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">{prefix}</span>}
        <Input inputMode="decimal" value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
          className={`rounded-xl ${prefix ? "pl-7" : ""} ${suffix ? "pr-10" : ""}`} data-testid={testid} />
        {suffix && <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">{suffix}</span>}
      </div>
    </div>
  );
}

export default function Calculator() {
  const [params] = useSearchParams();
  const [price, setPrice] = useState(params.get("price") || "");
  const [down, setDown] = useState("");
  const [rate, setRate] = useState("");
  const [term, setTerm] = useState("");
  const [hoa, setHoa] = useState("");
  const [tax, setTax] = useState("");
  const [ins, setIns] = useState("");

  const result = useMemo(() => {
    const p = num(price), d = num(down), r = num(rate), t = num(term);
    if (!(p > 0 && r > 0 && t > 0)) return null;
    const principal = Math.max(p - d, 0);
    const mr = r / 100 / 12;
    const n = t * 12;
    const pi = mr > 0 ? (principal * mr * Math.pow(1 + mr, n)) / (Math.pow(1 + mr, n) - 1) : principal / n;
    const extras = num(hoa) + num(tax) + num(ins);
    return { pi, extras, total: pi + extras, principal };
  }, [price, down, rate, term, hoa, tax, ins]);

  return (
    <div>
      <PageHeader title="Payment Calculator" subtitle="Estimate a monthly payment using your own numbers. Nothing is pre-filled." icon={CalcIcon} />

      <div className="grid lg:grid-cols-2 gap-6">
        <Card className="p-6 rounded-2xl space-y-4" data-testid="calc-inputs">
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Home price" prefix="$" value={price} onChange={setPrice} placeholder="600000" testid="calc-price" />
            <Field label="Down payment" prefix="$" value={down} onChange={setDown} placeholder="60000" testid="calc-down" />
            <Field label="Interest rate" suffix="%" value={rate} onChange={setRate} placeholder="from your lender" testid="calc-rate" />
            <Field label="Loan term" suffix="yrs" value={term} onChange={setTerm} placeholder="30" testid="calc-term" />
            <Field label="HOA / maintenance (monthly)" prefix="$" value={hoa} onChange={setHoa} placeholder="optional" testid="calc-hoa" />
            <Field label="Property tax (monthly)" prefix="$" value={tax} onChange={setTax} placeholder="optional" testid="calc-tax" />
            <Field label="Insurance (monthly)" prefix="$" value={ins} onChange={setIns} placeholder="optional" testid="calc-ins" />
          </div>
          <p className="text-xs text-muted-foreground">No rates are assumed — enter the numbers your lender gives you.</p>
        </Card>

        <Card className="p-6 rounded-2xl" data-testid="calc-result">
          {!result ? (
            <div className="h-full flex flex-col items-center justify-center text-center py-10" data-testid="calc-empty">
              <CalcIcon className="h-10 w-10 text-muted-foreground mb-3" />
              <p className="font-heading text-lg font-semibold">Enter your lender's numbers</p>
              <p className="text-sm text-muted-foreground mt-1">Add home price, interest rate and loan term to see an estimate.</p>
            </div>
          ) : (
            <div>
              <p className="text-sm text-muted-foreground">Estimated total monthly payment</p>
              <p className="font-heading text-4xl font-bold mt-1" data-testid="calc-total">{money(result.total)}</p>
              <div className="mt-5 space-y-2 text-sm">
                <Row label="Principal & interest" value={money(result.pi)} />
                {num(hoa) > 0 && <Row label="HOA / maintenance" value={money(num(hoa))} />}
                {num(tax) > 0 && <Row label="Property tax" value={money(num(tax))} />}
                {num(ins) > 0 && <Row label="Insurance" value={money(num(ins))} />}
                <div className="border-t border-border pt-2 flex justify-between font-semibold"><span>Total</span><span>{money(result.total)}</span></div>
              </div>
              <p className="text-xs text-muted-foreground mt-4">Loan amount {money(result.principal)} (price minus down payment).</p>
              <p className="text-xs font-medium text-foreground mt-3" data-testid="calc-disclaimer">Estimate only, not a loan quote.</p>
            </div>
          )}
        </Card>
      </div>

      <div className="mt-8">
        <Disclaimer>This calculator uses only the numbers you enter and assumes a fixed-rate loan. It does not include PMI, closing costs or rate changes, and is NOT a loan quote. Confirm real figures with your lender.</Disclaimer>
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return <div className="flex justify-between"><span className="text-muted-foreground">{label}</span><span className="font-medium">{value}</span></div>;
}
